import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {MARKET_QUOTES, noPayload, toAbsoluteUrl, URL_FIXED_PARAMS} from '../'
import {
    BetQueryResponseContextProps,
    BookieQueryResponseContextProps,
    CompetitionQueryResponseContextProps,
    CountryQueryResponseContextProps,
    EventQueryResponseContextProps,
    ID,
    initialQueryState,
    ParticipantQueryResponseContextProps,
    PickQueryResponseContextProps,
    QueryResponseContextProps,
    QueryState,
    SelectOption,
    ServiceQueryResponseContextProps,
    SportQueryResponseContextProps,
    TQueryResponseContextProps,
    TTQueryResponseContextProps
} from './models'
import {
    Advertisement,
    Article,
    Bookie,
    Country,
    Discount,
    Event,
    HomeEvent,
    HomePromoCard,
    Market,
    Message,
    Participant,
    PickResultType,
    PickStatus,
    Sport,
    Subscription,
    TipsterType,
    User
} from '@apuestes/types';
import axios from 'axios';

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createTipsterResponseContext<T>(initialState: TQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createTipsterTypeResponseContext<T>(initialState: TTQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createCountryResponseContext<T>(initialState: CountryQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createSportResponseContext<T>(initialState: SportQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createCompetitionResponseContext<T>(initialState: CompetitionQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createPartiucipantsResponseContext<T>(initialState: ParticipantQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createBookieResponseContext<T>(initialState: BookieQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createBetResponseContext<T>(initialState: BetQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createPickResponseContext<T>(initialState: PickQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createEventResponseContext<T>(initialState: EventQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function createServiceResponseContext<T>(initialState: ServiceQueryResponseContextProps<T>) {
    return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
    return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
    const pagination = qs.stringify(state, {filter: ['page', 'limit'], skipNulls: false})
    const sort = qs.stringify(state, {filter: ['sort', 'order'], skipNulls: false})
    const search = isNotEmpty(state.search)
        ? qs.stringify(state, {filter: ['search'], skipNulls: true})
        : ''

    const filter = state.filter
        ? Object.entries(state.filter as Object)
            .filter((obj) => isNotEmpty(obj[1]))
            .map((obj) => {
                return `${obj[0]}=${obj[1]}`
            })
            .join('&')
        : ''

    return [pagination, sort, search, filter]
        .filter((f) => f)
        .join('&')
}

function parseRequestQuery(query: string): QueryState {
    const cache: unknown = qs.parse(query)
    return cache as QueryState
}

// Construye el estado de paginación (page + links) a partir de la respuesta del API,
// que expone `current_page` y `pages` a nivel raíz (ver PaginateInterceptor del backend).
function buildPaginationState(
    base: {links?: Array<{label: string; active: boolean; url: string | null; page: number | null}>; page: number; limit: any},
    currentPage?: number,
    totalPages?: number,
) {
    const page = currentPage && currentPage > 0 ? currentPage : 1
    const pages = totalPages && totalPages > 0 ? totalPages : 1

    if (pages <= 1) {
        return {...base, page, links: []}
    }

    const links: Array<{label: string; active: boolean; url: string | null; page: number | null}> = [
        {label: '&laquo; Previous', active: false, url: null, page: page > 1 ? page - 1 : null},
        ...Array.from({length: pages}, (_, index) => ({
            label: `${index + 1}`,
            active: page === index + 1,
            url: null,
            page: index + 1,
        })),
        {label: 'Next &raquo;', active: false, url: null, page: page < pages ? page + 1 : null},
    ]

    return {...base, page, links}
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
    if (isLoading) {
        return true
    }

    return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
    if (!data) {
        return false
    }

    if (window.location.href.includes('/user-management/users') || window.location.href.includes('/tipster-management/tipsters')){
        return data.length > 0 && data.length - 1 === selected.length
    } else {
        return data.length > 0 && data.length === selected.length
    }
}

function groupingOnSelect(
    id: ID,
    selected: Array<ID>,
    setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
    if (!id) {
        return
    }

    if (selected.includes(id)) {
        setSelected(selected.filter((itemId) => itemId !== id))
    } else {
        const updatedSelected = [...selected]
        updatedSelected.push(id)
        setSelected(updatedSelected)
    }
}

function groupingOnSelectAll<T>(
    isAllSelected: boolean,
    setSelected: Dispatch<SetStateAction<Array<ID>>>,
    currentUser: any,
    data?: Array<T & {id?: ID}>
) {
    if (isAllSelected) {
        setSelected([])
        return
    }

    if (!data || !data.length) {
        return
    }
    if (window.location.href.includes('/user-management/users') || window.location.href.includes('/tipster-management/tipsters')){
        const excludeCurrentUser = data.filter((item) => item.id).map((item) => item.id).filter(function(value, index, arr){
            return value !== currentUser.id;
        });
        setSelected(excludeCurrentUser)
    } else {
        setSelected(data.filter((item) => item.id).map((item) => item.id))
    }
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value)
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value)
            }, delay)
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler)
            }
        },
        [value, delay] // Only re-call effect if value or delay changes
    )
    return debouncedValue
}

function useDynamicLinks(id: number, type: string) {
    const [response, setResponse] = useState<any>(null)
    const [errors, setErrors] = useState<any>(null)

    const fetchLink = () => {
        return axios.post(
            `https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=${ process.env.REACT_APP_API_DYNAMIC_LINKS_KEY }`,
            {
                "longDynamicLink": `${ process.env.REACT_APP_FIREBASE_DYNAMIC_LINKS }?link=${ process.env.REACT_APP_URI_PICK_LINK }/${ type }?id=${ id }&apn=${ process.env.REACT_APP_BUNDLE_ID }&ibi=${ process.env.REACT_APP_BUNDLE_ID }`
            }
        )
            .then( response => setResponse(response) )
            .catch( errors => setErrors(errors) )
    }

    useEffect( () => {
        const shortLink =async () => {
            await fetchLink()
        }
        shortLink()
        // eslint-disable-next-line
    }, [] )

    return { response, errors }
}

function convertBase64(file: File) {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
            resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
            reject(error);
        };
    });
}

function getPhoto(type: string, name: string | null, size: string = 'small'): string|undefined {
    if (type && name && process.env.REACT_APP_S3_URL) {
        return size ? process.env.REACT_APP_S3_URL + type + '/' + size + '.' + name : process.env.REACT_APP_S3_URL + type + '/' + name
    } else {
        // Use banner placeholder for advertisements
        if (type === 'banner' || type === 'advertisements') {
            return toAbsoluteUrl(`/media/banners/blank-banner.png`)
        }
        return toAbsoluteUrl(`/media/avatars/blank.png`)
    }
}

const onSendValues = async (
    models: string,
    values: User | TipsterType | Sport | Participant | Country | Bookie | Event | Market | Discount | Message | Advertisement | Subscription | HomePromoCard | Article | HomeEvent | Record<string, unknown>,
    exceptKeys: Array<string|null> = [],
    fn: Function,
    id?: ID
) => {
    const payload_values = cleanModel(values, exceptKeys)
    return await fn({model: models, id: id, payload: payload_values});
}

const onSendMedia = async (
    id: ID,
    propertyName: string,
    models: string,
    fn: Function,
    image: HTMLInputElement | File | null
) => {
    if (!image) {
        console.error('No file selected');
        return;
    }

    const payload = new FormData();

    if (image instanceof HTMLInputElement) {
        if (!image.files) {
            console.warn('No files property on input element');
            return;
        }
        if (image.files.length === 0) {
            console.warn('Warning: No files selected');
            return;
        }
        payload.append(propertyName, image.files[0]);
    } else if (image instanceof File) {
        payload.append(propertyName, image);
    } else {
        console.error('Invalid image type provided');
        return;
    }

    await fn({ model: models, id, payload });
};

const cancel = (withRefresh: boolean, refetch: Function, setItem: Function) => {
    if (withRefresh) {
        refetch()
    }
    setItem(undefined)
}

const deleteSelected = (model: string, ids: Array<ID>): Promise<void> => {
    const requests = ids.map((id) => axios.delete(`${process.env.REACT_APP_API_URL}/${model}/${id}`))
    return axios.all(requests).then(() => {})
}

const cleanModel = (model: User | TipsterType | Sport | Participant | Country | Bookie | Event | Market | Advertisement | Discount | Message | Subscription | HomePromoCard | Article | HomeEvent | Record<string, unknown>, exceptKeys: Array<string|null> = []) => {
    // eslint-disable-next-line array-callback-return
    const cleanedModel = { ...model };
    Object.keys(model).forEach((key) => {
        if (noPayload.includes(key)) {
            if (!exceptKeys.includes(key)) {
                delete (cleanedModel as any)[key];
            }
        }
    });
    return cleanedModel;
}

const createPayload = (data: URLSearchParams, remove?: Array<string>, method?: string) => {
    const payload: { [key: string]: string | boolean | number | any[] } = {};
    if (data.has('post')) data.delete('post');
    return {
        payload : () => {
            for (const key of data.keys()) {
                if (!URL_FIXED_PARAMS.includes(key)) {
                    let value = data.get(key);
                    if (value === 'true') {
                        Object.assign(payload, { [key]: true });
                    } else if (value === 'false') {
                        Object.assign(payload, { [key]: false });
                    } else if (value && !isNaN(Number(value))) {
                        Object.assign(payload, { [key]: Number(value) });
                    } else if (value && value.startsWith('[') && value.endsWith(']')) {
                        Object.assign(payload, { [key]: JSON.parse(value) });
                    } else {
                        Object.assign(payload, { [key]: value });
                    }
                }
            };
            return payload;
        },
        newQuery: () => {
            if (data.has('page') && data.has('limit')) {
                Array.from(data.entries()).forEach(([key, value]) => {
                    if (!URL_FIXED_PARAMS.includes(key)) {
                        data.delete(key);
                    }
                });
                return data.toString();
            } else {
                return stringifyRequestQuery(initialQueryState)
            }
        }
    };
}

const getColorType = (type: string) => {
    switch (type?.toLocaleLowerCase()) {
        case 'vip': return'dark';
        case 'gold': return'warning';
        case 'premium': return'info';
        case 'freemium': return'primary';
        default: return 'primary';
    }
}

const findReactComponent = (el: any) => {
    for (const key in el) {
        if (key.startsWith('__reactFiber$')) {
            const fiberNode = el[key];

            return fiberNode;
        }
    }
    return null;
};

const getMarketQuotes = (label:string) => {
    const id = MARKET_QUOTES.filter( (item: any) => item.label === label)[0].value
    return id
}

const getSportResultQuotes = (label:string) => {
    const id = transformResultTypesSelect().filter( (item: any) => item.label === label)[0].value
    return id
}

const transformResultTypesSelect = () => {
    let arr: Array<SelectOption> = []
    Object.values(PickResultType).forEach( (item, idx) => {
        arr.push({ label: item, value: idx })
    } )

    return arr;
}

const transformStatusTypesSelect = () => {
    let arr: Array<SelectOption> = []
    Object.values(PickStatus).forEach( (item, idx) => {
        arr.push({ label: item, value: idx })
    } )

    return arr;
}

const getPackageVersion = () => {
    var json_app = require('../../../../package.json');

    return json_app.version
}

const getErrorMessage = (error: any): string => {
    let message = '';
    Object.entries((error as any).data?.errors).forEach(async ([key, value]) => {
        if (key) {
            message += `${value} \n`
        }
    })

  return message;
}

export {
  createResponseContext,
  findReactComponent,
  getSportResultQuotes,
  createTipsterResponseContext,
  createSportResponseContext,
  createTipsterTypeResponseContext,
  createCountryResponseContext,
  createEventResponseContext,
  createCompetitionResponseContext,
  createPartiucipantsResponseContext,
  createBookieResponseContext,
  createBetResponseContext,
  createPickResponseContext,
  createServiceResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  buildPaginationState,
  createPayload,
  onSendValues,
  onSendMedia,
  cancel,
  getErrorMessage,
  transformResultTypesSelect,
  transformStatusTypesSelect,
  deleteSelected,
  getColorType,
  getMarketQuotes,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  useDynamicLinks,
  isNotEmpty,
  convertBase64,
  getPhoto,
  cleanModel,
  getPackageVersion
}
